import React, { ReactNode } from "react";
import { Box, Flex, Grid, Image, Text } from "@chakra-ui/react";
import StatusBadge from "@/shared/components/StatusBadge";
import PathPickAiImageBadge from "@/features/pathpick/components/product/PathPickAiImageBadge";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  DEFAULT_ORDER_STATUS_COLOR_VARIANT,
  ORDER_HISTORY_KEY,
  ORDER_HISTORY_TEMPLATE_COLUMNS,
  ORDER_STATUS_META,
} from "@/features/pathpick/features/order-history/constants";
import { PathPickOrder } from "@/features/pathpick/features/order-history/types/response";
import {
  getPathPickOrderItemImageUrl,
  isPathPickOrderItemImageAiCollected,
  formatPathPickOrderDate,
  selectPathPickOrderTotalsView,
} from "@/features/pathpick/features/order-history/utils";

interface Props {
  order: PathPickOrder;
  onClick?: () => void;
}

// 총 수량/주문 금액 셀 — 확정값이 있으면 주문값을 취소선으로 위에 남긴다(시안).
function StackedTotalCell({
  ordered,
  confirmed,
}: {
  ordered: string;
  confirmed: string | null;
}) {
  if (confirmed == null) {
    return (
      <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
        {ordered}
      </Text>
    );
  }
  return (
    <Flex flexDirection={"column"} gap={"1px"}>
      <Text
        textStyle={"caption-regular"}
        color={"#6F6F6C"}
        textDecoration={"line-through"}
      >
        {ordered}
      </Text>
      <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
        {confirmed}
      </Text>
    </Flex>
  );
}

// 상품 셀(대표 이미지 + 대표 SKU + "N개 · 브랜드") — 테이블 행과 모바일 카드가 공유한다.
function OrderProductCell({ order }: { order: PathPickOrder }) {
  const { t } = useI18nHelper();

  return (
    <Flex gap={"12px"} alignItems={"center"} minWidth={0}>
      <Box position={"relative"} flexShrink={0}>
        <Image
          src={getPathPickOrderItemImageUrl(order.items[0]) ?? ""}
          fallbackSrc={"/icons-v1_1/null.png"}
          boxSize={"48px"}
          objectFit={"contain"}
          borderRadius={"8px"}
          border={"0.5px solid #EFEFED"}
          background={"#FFFFFF"}
          alt={order.representativeSku}
        />
        {isPathPickOrderItemImageAiCollected(order.items[0]) && (
          <PathPickAiImageBadge size={"small"} />
        )}
      </Box>
      <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
        <Text
          textStyle={"body-md-bold"}
          color={"#1D1D1B"}
          noOfLines={1}
          wordBreak={"break-all"}
        >
          {order.representativeSku}
        </Text>
        <Text
          textStyle={"caption-medium"}
          color={"#8F8F8C"}
          noOfLines={1}
          wordBreak={"break-all"}
        >
          {t("path_pick:order_history.product_summary", {
            count: order.itemCount,
            brands: order.brandNames.join(", "),
          })}
        </Text>
      </Flex>
    </Flex>
  );
}

// 모바일 카드의 라벨-값 한 칸. 라벨은 테이블 헤더와 같은 i18n 키를 쓴다.
function MobileStatField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      {children}
    </Flex>
  );
}

function PathPickOrderHistoryListItem({ order, onClick }: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  // 매핑되지 않은 상태가 내려오면 원문 status를 그대로 노출한다.
  const statusMeta = ORDER_STATUS_META[order.status];
  const statusLabel = statusMeta ? t(statusMeta.labelKey) : order.status;
  const statusColorVariant =
    statusMeta?.colorVariant ?? DEFAULT_ORDER_STATUS_COLOR_VARIANT;
  // 확정 후에는 주문값(취소선)과 확정값을 세로로 같이 보여준다.
  const totals = selectPathPickOrderTotalsView(order);

  const quantityText = (count: number | null) =>
    count == null
      ? "-"
      : t("path_pick:order_history.quantity_value", { count });
  const amountText = (value: number | null) =>
    value == null
      ? "-"
      : tCurrencyString(value, { type: "SYMBOL", code: order.currency });

  // 셀 내용은 두 레이아웃이 공유하고, 래퍼만 폭에 따라 갈린다.
  const orderedAtText = formatPathPickOrderDate(order.orderedAt);
  const quantityCell = (
    <StackedTotalCell
      ordered={quantityText(totals.orderedQuantity)}
      confirmed={
        totals.confirmed ? quantityText(totals.confirmed.quantity) : null
      }
    />
  );
  const amountCell = (
    <StackedTotalCell
      ordered={amountText(totals.orderedAmount)}
      confirmed={totals.confirmed ? amountText(totals.confirmed.amount) : null}
    />
  );
  const statusBadge = (
    <StatusBadge status={statusLabel} colorVariant={statusColorVariant} />
  );

  // 두 래퍼가 공유하는 카드 셸 스타일 — 한쪽만 바뀌어 어긋나지 않게 묶어 둔다.
  const shellProps = {
    padding: "16px",
    border: "0.5px solid #D4D4D1",
    borderRadius: "8px",
    background: "#FFFFFF",
    cursor: onClick ? "pointer" : "default",
    onClick,
    _hover: onClick
      ? { boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.10)" }
      : undefined,
  } as const;

  return (
    <>
      {/* 데스크탑 — 6열 테이블 행(헤더와 같은 templateColumns를 공유한다) */}
      <Grid
        display={{ base: "none", md: "grid" }}
        templateColumns={ORDER_HISTORY_TEMPLATE_COLUMNS}
        gap={"16px"}
        alignItems={"center"}
        {...shellProps}
      >
        <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
          {order.orderNumber}
        </Text>

        <OrderProductCell order={order} />

        <Text textStyle={"body-sm-medium"} color={"#444440"}>
          {orderedAtText}
        </Text>

        {quantityCell}

        {amountCell}

        <Flex>{statusBadge}</Flex>
      </Grid>

      {/* 모바일 — 같은 값을 카드로 재배치한다. 6열 표는 최소 1092px라 좁은 폭에서
          가로로 밀어야 읽히는데, 행 전체가 상세로 가는 탭 타깃이라 스크롤 제스처와
          탭이 같은 표면에서 충돌한다. */}
      <Flex
        display={{ base: "flex", md: "none" }}
        flexDirection={"column"}
        gap={"12px"}
        {...shellProps}
      >
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={"8px"}
        >
          <Text
            textStyle={"body-md-bold"}
            color={"#1D1D1B"}
            noOfLines={1}
            wordBreak={"break-all"}
          >
            {order.orderNumber}
          </Text>
          <Flex flexShrink={0}>{statusBadge}</Flex>
        </Flex>

        <OrderProductCell order={order} />

        <Grid templateColumns={"repeat(3, minmax(0, 1fr))"} gap={"8px"}>
          <MobileStatField label={t(`${ORDER_HISTORY_KEY}column_order_date`)}>
            <Text textStyle={"body-sm-medium"} color={"#444440"}>
              {orderedAtText}
            </Text>
          </MobileStatField>
          <MobileStatField label={t(`${ORDER_HISTORY_KEY}column_quantity`)}>
            {quantityCell}
          </MobileStatField>
          <MobileStatField label={t(`${ORDER_HISTORY_KEY}column_amount`)}>
            {amountCell}
          </MobileStatField>
        </Grid>
      </Flex>
    </>
  );
}

export default PathPickOrderHistoryListItem;
